import { useEffect, useMemo, useState } from "react"
import PubSub from "pubsub-js"
import { QRCodeCanvas } from "qrcode.react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import Spinner from "aldoo-ra/Spinner"
import Input from "aldoo-ra/Input"
import { showSuccessToast, showErrorToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import {
    ClockIcon, SwatchIcon, UserGroupIcon, KeyIcon, EyeIcon, EyeSlashIcon,
    PencilSquareIcon, ChatBubbleLeftIcon, GlobeAltIcon, LockClosedIcon,
    UsersIcon, ClipboardDocumentIcon, PauseIcon, PlayIcon, TrashIcon,
    HashtagIcon, InboxArrowDownIcon, ExclamationTriangleIcon,
    QrCodeIcon, CheckIcon, ArrowDownTrayIcon, EnvelopeIcon, DocumentDuplicateIcon,
    ArrowTopRightOnSquareIcon, FolderArrowDownIcon, FolderIcon,
    ChatBubbleLeftEllipsisIcon, ShareIcon,
} from "@heroicons/react/24/outline"
import { motion, AnimatePresence } from "framer-motion"
import Drawer from "../FileShareDashboard/Drawer"
import StatusBadge from "../FileShareDashboard/StatusBadge"
import Modal from "aldoo-ra/Modal"
import FileTree from "../FileShareFeature/FileTree"
import UserAccessWidget from "../UserAccessWidget"
import LinkSender from "../FileShareFeature/LinkSender"
import ThemeList from "../ShareThemes/ThemeList"
import { ACCOUNT_DEFAULT_ID } from "../ShareThemes/constants"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import { updateLink, setLinkStatus, listLinkTransfers, sendLinkInvite } from "./controller"
import { DeleteFileShare, GetShareDownloadUrl, PrepareDownloadShareAll, PrepareDownloadShareFolder } from "../FileShareFeature/api"

// View + edit drawer for a receive link. 1:1 with the File Share Options
// drawer: status chips at top, action row, tabs that double as inline
// editors. Each tab calls updateLink({...}) to PATCH the field and shows a
// toast on success.

function formatDate(d) {
    if (!d) return ""
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
}

function transferTimeRemaining(t) {
    if (!t?.expires_at || t.status === "expired") return null
    const diff = new Date(t.expires_at) - new Date()
    if (diff <= 0) return null
    const day = 86400000
    if (diff >= day) {
        const d = Math.ceil(diff / day)
        return `${d} day${d === 1 ? "" : "s"} left`
    }
    const h = Math.max(1, Math.ceil(diff / 3600000))
    return `${h}h left`
}

function formatBytes(bytes) {
    if (!bytes) return "0 B"
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function effectiveStatus(link) {
    if (!link) return ""
    if (link.status === "active" && new Date(link.link_expires_at).getTime() <= Date.now()) {
        return "expired"
    }
    return link.status
}

export default function ReceiveLinkDrawer({ link, onClose, onChanged, onDeleted }) {
    const open = !!link

    const [tab, setTab] = useState("transfers")

    // Local working state, hydrated from the link prop. Each patch sends just
    // the changed fields so concurrent edits don't clobber each other.
    const [name, setName] = useState(link?.name || "")
    const [senderMessage, setSenderMessage] = useState(link?.sender_message || "")
    const [themeId, setThemeId] = useState(link?.presets?.theme_id || ACCOUNT_DEFAULT_ID)
    const [accessType, setAccessType] = useState(link?.presets?.access_type || "public")
    const [accessEmails, setAccessEmails] = useState(link?.presets?.access || [])
    const [showAccessDrawer, setShowAccessDrawer] = useState(false)
    const [passwordEnabled, setPasswordEnabled] = useState(!!link?.presets?.password_set)
    const [hasPassword, setHasPassword] = useState(!!link?.presets?.password_set)
    const [password, setPassword] = useState("")
    const [showPassword, setShowPassword] = useState(false)
    const [notifyOwner, setNotifyOwner] = useState(link?.notify_owner !== false)
    const [savingTab, setSavingTab] = useState(null)
    const [copiedToken, setCopiedToken] = useState(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [qrOpen, setQrOpen] = useState(false)
    const [inviteOpen, setInviteOpen] = useState(false)
    const [inviteEmails, setInviteEmails] = useState([])
    const [inviteMessage, setInviteMessage] = useState("")
    const [inviteSending, setInviteSending] = useState(false)
    const [inviteMessageOpen, setInviteMessageOpen] = useState(false)
    const [transfers, setTransfers] = useState([])
    const [loadingTransfers, setLoadingTransfers] = useState(false)
    const [filesModal, setFilesModal] = useState(null) // active transfer for the file-list modal
    const [filesDownloading, setFilesDownloading] = useState(false)
    const [detailsTransfer, setDetailsTransfer] = useState(null) // sender title + notes overlay
    const [deletingOrderID, setDeletingOrderID] = useState(null)

    const texts = TextBundle({ id: "transfer_receive_links" })
    const { translateWithLanguage, getLanguage } = Localization()
    const t = (key, data) => translateWithLanguage(key, getLanguage(), data)

    const handleDeleteTransfer = async (transfer) => {
        const senderLabel = transfer.sender_name || transfer.sender_email || texts?.unknownSender
        const ok = await showPromptDialogAsync({
            title: texts?.deleteTransferTitle,
            message: t(texts?.deleteTransferMsg, { sender: senderLabel }),
            yesLabel: texts?.delete,
            noLabel: texts?.cancel,
        })
        if (!ok) return
        setDeletingOrderID(t.orderID)
        try {
            const res = await DeleteFileShare(t.orderID)
            if (res?.result) {
                setTransfers((prev) => prev.filter((x) => x.orderID !== t.orderID))
            }
        } finally {
            setDeletingOrderID(null)
        }
    }

    // Refetch transfers each time the drawer opens a new link or the user
    // switches to the Transfers tab. Cheap query, owner-scoped.
    useEffect(() => {
        if (!link?._id || tab !== "transfers") return
        let cancel = false
        const fetchTransfers = () => {
            setLoadingTransfers(true)
            listLinkTransfers(link._id).then((res) => {
                if (cancel) return
                if (res?.result) setTransfers(res.transfers || [])
            }).finally(() => { if (!cancel) setLoadingTransfers(false) })
        }
        fetchTransfers()

        // Realtime: refetch when a new transfer lands or scan completes
        // for an order owned by this link. Server payload includes the
        // linkId so we filter.
        const onReceived = (_, payload) => {
            if (String(payload?.receiver_link_id) === String(link._id)) fetchTransfers()
        }
        const onScan = () => fetchTransfers()
        const t1 = PubSub.subscribe("transfer.share.received", onReceived)
        const t2 = PubSub.subscribe("transfer.share.scan", onScan)
        const t3 = PubSub.subscribe("transfer.share.deleted", onScan)

        return () => {
            cancel = true
            PubSub.unsubscribe(t1)
            PubSub.unsubscribe(t2)
            PubSub.unsubscribe(t3)
        }
    }, [link?._id, tab])

    // Re-hydrate state when a different link is opened, or when the parent
    // pushes a fresh copy after a successful save / realtime update.
    //
    // Dep set includes every field the local state mirrors — keying only on
    // `_id` meant a refreshed link object with the same id (e.g. notify_owner
    // flipped from another device) never re-seeded the toggle, so the drawer
    // looked stuck on the old value.
    useEffect(() => {
        if (!link) return
        setName(link.name || "")
        setSenderMessage(link.sender_message || "")
        setThemeId(link.presets?.theme_id || ACCOUNT_DEFAULT_ID)
        setAccessType(link.presets?.access_type || "public")
        setAccessEmails(link.presets?.access || [])
        setPasswordEnabled(!!link.presets?.password_set)
        setHasPassword(!!link.presets?.password_set)
        setNotifyOwner(link.notify_owner !== false)
    }, [
        link?._id,
        link?.notify_owner,
        link?.name,
        link?.sender_message,
        link?.presets?.theme_id,
        link?.presets?.access_type,
        link?.presets?.password_set,
    ])

    const url = useMemo(() => {
        if (!link) return ""
        const origin = typeof window !== "undefined" ? window.location.origin : ""
        return `${origin}/r/${link.token}`
    }, [link])
    const tokenShort = link?.token ? link.token.slice(0, 12) : ""

    const isExpired = link?.status === "expired" || (link?.link_expires_at && new Date(link.link_expires_at).getTime() <= Date.now())
    const isPaused = link?.status === "paused"

    const patch = async (key, payload) => {
        if (!link) return
        setSavingTab(key)
        try {
            const res = await updateLink(link._id, payload)
            if (res?.error) {
                showErrorToast(texts?.saveFailed, res.error, {
                    position: TOAST_POSITIONS.TOP_CENTER, pauseOnHover: false, enableDismissalTracking: false,
                })
                return false
            }
            if (res?.result) {
                onChanged?.(res.link)
                showSuccessToast(texts?.saved, "", {
                    position: TOAST_POSITIONS.TOP_CENTER, pauseOnHover: false,
                    enableDismissalTracking: false, duration: 1200,
                })
                return true
            }
        } finally {
            setSavingTab(null)
        }
        return false
    }

    const handleCopy = () => {
        try {
            navigator.clipboard.writeText(url)
            setCopiedToken(link.token)
            setTimeout(() => setCopiedToken(null), 1200)
        } catch {}
    }

    const togglePause = async () => {
        const op = link.status === "active" ? "pause" : "resume"
        const res = await setLinkStatus(link._id, op)
        if (res?.result) onChanged?.(res.link)
    }

    const remove = async () => {
        const ok = await showPromptDialogAsync({
            title: texts?.deleteLinkTitle,
            message: texts?.deleteLinkMsg,
            yesLabel: texts?.delete,
            noLabel: texts?.cancel,
        })
        if (!ok) return
        setIsDeleting(true)
        try {
            const res = await setLinkStatus(link._id, "delete")
            if (res?.result) {
                onDeleted?.(link._id)
                onClose?.()
            }
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            leftAction={
                <Typography variant="txt1_accent" className="truncate max-w-[320px] text-center">
                    {link?.name || texts?.untitledLink}
                </Typography>
            }
            overlay={
                <div
                    className={`absolute inset-0 z-10 bg-paper flex flex-col items-center justify-center gap-4 transition-transform duration-300 ${qrOpen ? "translate-x-0" : "translate-x-full"}`}
                    onClick={() => setQrOpen(false)}
                >
                    <p className="text-sm font-medium text-text_secondary">
                        {texts?.scanToOpen}
                    </p>
                    {qrOpen && (
                        <QRCodeCanvas value={url} size={220} level="H" marginSize={1} />
                    )}
                    <p className="text-xs text-text_secondary/60 max-w-[220px] text-center break-all">
                        {url}
                    </p>
                    <button
                        onClick={() => setQrOpen(false)}
                        className="text-xs text-text_secondary hover:text-text transition-colors mt-1"
                    >
                        {texts?.tapToClose}
                    </button>
                </div>
            }
        >
            {link && (
                <div className="px-5 py-4 flex flex-col gap-4">
                    {/* Status card — one neutral card; color coding lives on
                        the pause/play button only (same as ShareDrawer). */}
                    {(() => {
                        const s = (link.status || "").toLowerCase()
                        let dot = "bg-progress", btn = "bg-progress"
                        let label = link.status || "Unknown"
                        if (isExpired) { dot = "bg-error"; btn = "bg-error"; label = texts?.expired }
                        else if (s === "active") { dot = "bg-success"; btn = "bg-success"; label = texts?.receiveOn }
                        else if (s === "paused") { dot = "bg-warning"; btn = "bg-warning"; label = texts?.receiveOff }
                        const clickable = !isExpired && (s === "active" || s === "paused")
                        return (
                            <div className="flex items-center justify-between bg-neutral rounded-xl px-4 py-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                                    <span className="text-sm font-semibold text-text">{label}</span>
                                </div>
                                {clickable && (
                                    <button
                                        type="button"
                                        onClick={() => togglePause()}
                                        // Color reflects action target (iOS parity):
                                        // resume → green, pause → orange.
                                        className={`h-9 w-9 rounded-full text-text_inv flex items-center justify-center hover:brightness-110 ${isPaused ? 'bg-success' : 'bg-warning'}`}
                                    >
                                        {isPaused ? <PlayIcon className="h-5 w-5 stroke-2" /> : <PauseIcon className="h-5 w-5 stroke-2" />}
                                    </button>
                                )}
                            </div>
                        )
                    })()}

                    {/* Notify — same neutral-card pattern as the other rows. */}
                    <div className="flex items-center justify-between bg-neutral rounded-xl px-4 py-3">
                        <div className="flex flex-col">
                            <span className="text-sm font-semibold text-text flex items-center gap-1.5">
                                <InboxArrowDownIcon className="h-4 w-4 text-text_secondary" />
                                {texts?.notifyOwner}
                            </span>
                            <span className="text-xs text-text_secondary mt-0.5">
                                {texts?.notifyOwnerHelp}
                            </span>
                        </div>
                        <ToggleSwitch
                            isOn={notifyOwner}
                            onChange={async (val) => {
                                setNotifyOwner(val)
                                await patch("notify", { notify_owner: val })
                            }}
                        />
                    </div>

                    {/* Copy / QR / Share — 1:1 with the File Share Options
                        action grid (without the Download tile, since /r
                        URLs are upload-only). */}
                    {!isExpired && (
                        <div className="grid grid-cols-4 gap-2">
                            <button
                                onClick={() => { setInviteEmails([]); setInviteMessage(""); setInviteMessageOpen(false); setInviteOpen(true) }}
                                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
                            >
                                <EnvelopeIcon className="h-5 w-5 text-text_secondary" />
                                <span className="text-xs font-medium text-text_secondary">{texts?.sendOverEmail}</span>
                            </button>
                            <button
                                onClick={handleCopy}
                                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
                            >
                                {copiedToken === link.token ? (
                                    <>
                                        <CheckIcon className="h-5 w-5 text-primary" />
                                        <span className="text-xs font-medium text-primary">{texts?.copied}</span>
                                    </>
                                ) : (
                                    <>
                                        <ClipboardDocumentIcon className="h-5 w-5 text-text_secondary" />
                                        <span className="text-xs font-medium text-text_secondary">{texts?.copyLink}</span>
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => setQrOpen(true)}
                                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
                            >
                                <QrCodeIcon className="h-5 w-5 text-text_secondary" />
                                <span className="text-xs font-medium text-text_secondary">{texts?.qrCode}</span>
                            </button>
                            <button
                                onClick={async () => {
                                    if (typeof navigator !== "undefined" && navigator.share) {
                                        try {
                                            await navigator.share({ title: link.name || "Send me files", url })
                                            return
                                        } catch {}
                                    }
                                    handleCopy()
                                }}
                                className="flex flex-col items-center justify-center gap-1.5 bg-neutral rounded-xl px-4 py-3 hover:bg-neutral_1 transition-colors"
                            >
                                <ShareIcon className="h-5 w-5 text-text_secondary" />
                                <span className="text-xs font-medium text-text_secondary">{texts?.share}</span>
                            </button>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="flex border-b border-bordered -mx-5 px-5 overflow-x-auto no-scrollbar">
                        {[
                            { id: "transfers", label: texts?.transfers, icon: DocumentDuplicateIcon },
                            { id: "notes",    label: texts?.notes,       icon: ChatBubbleLeftIcon },
                            { id: "access",   label: texts?.userAccess, icon: UserGroupIcon },
                            { id: "theme",    label: texts?.theme,       icon: SwatchIcon },
                            { id: "password", label: texts?.password, icon: KeyIcon },
                            { id: "properties", label: texts?.properties, icon: HashtagIcon },
                        ].map(({ id, label, icon: Icon }) => (
                            <button
                                key={id}
                                type="button"
                                onClick={() => setTab(id)}
                                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex-shrink-0 ${
                                    tab === id
                                        ? "border-primary text-primary"
                                        : "border-transparent text-text_secondary hover:text-text"
                                }`}
                            >
                                <Icon className="h-4 w-4" strokeWidth={2} />
                                <span className="hidden sm:inline">{label}</span>
                            </button>
                        ))}
                    </div>

                    {tab === "transfers" && (
                        <div className="pt-1 flex flex-col gap-2">
                            {loadingTransfers ? (
                                <div className="flex items-center justify-center py-10">
                                    <Spinner className="w-[16px] h-[16px]" />
                                </div>
                            ) : transfers.length === 0 ? (
                                <div className="bg-canvas border border-bordered border-dashed rounded-xl p-6 text-center">
                                    <Typography variant="txt_dim">
                                        {texts?.noInboundYet}
                                    </Typography>
                                </div>
                            ) : (
                                transfers.map((transfer) => {
                                    const openShare = () => {
                                        const origin = typeof window !== "undefined" ? window.location.origin : ""
                                        window.open(`${origin}/s/${transfer.share_token}`, "_blank", "noopener")
                                    }
                                    const triggerDownload = async () => {
                                        const r = await PrepareDownloadShareAll(transfer.share_token)
                                        if (r?.url) window.location.href = r.url
                                    }
                                    return (
                                        <div key={transfer.orderID} className="bg-neutral rounded-xl px-4 py-3 flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                                <EnvelopeIcon className="w-5 h-5" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-text truncate">
                                                    {transfer.sender_name || transfer.sender_email || (texts?.unknownSender)}
                                                </div>
                                                <div className="text-xs text-text_secondary truncate">
                                                    {formatBytes(transfer.file_size)}
                                                    {(() => {
                                                        const tr = transferTimeRemaining(transfer)
                                                        return tr ? <span className="ml-2">· {tr}</span> : null
                                                    })()}
                                                    {transfer.scan_pending && (
                                                        <span className="ml-2 text-warning">{texts?.scanning}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {(transfer.sender_title || transfer.sender_notes) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setDetailsTransfer(transfer)}
                                                    title={texts?.message}
                                                    className="p-1.5 rounded-lg text-text_secondary hover:text-text hover:bg-neutral_1"
                                                >
                                                    <ChatBubbleLeftEllipsisIcon className="w-5 h-5" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={triggerDownload}
                                                title={texts?.download}
                                                className="p-1.5 rounded-lg text-text_secondary hover:text-text hover:bg-neutral_1"
                                            >
                                                <ArrowDownTrayIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFilesModal(transfer)}
                                                title={texts?.seeFiles}
                                                className="p-1.5 rounded-lg text-text_secondary hover:text-text hover:bg-neutral_1"
                                            >
                                                <FolderIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteTransfer(transfer)}
                                                disabled={deletingOrderID === transfer.orderID}
                                                title={texts?.delete}
                                                className="p-1.5 rounded-lg text-text_secondary hover:text-error hover:bg-neutral_1 disabled:opacity-40"
                                            >
                                                {deletingOrderID === transfer.orderID
                                                    ? <Spinner className="w-[16px] h-[16px]" />
                                                    : <TrashIcon className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    )}

                    {tab === "notes" && (() => {
                        const dirty = name !== (link.name || "") || senderMessage !== (link.sender_message || "")
                        return (
                        <div className="pt-1 flex flex-col gap-3">
                            <label className="text-xs font-medium text-text_secondary">{texts?.title}</label>
                            <Input
                                variant="transfer_compact"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={texts?.linkNamePh}
                                className="w-full"
                            />
                            <label className="text-xs font-medium text-text_secondary">{texts?.message}</label>
                            <textarea
                                value={senderMessage}
                                onChange={(e) => setSenderMessage(e.target.value)}
                                rows={5}
                                placeholder={texts?.senderMessagePh}
                                className="w-full rounded-xl border border-bordered bg-transparent px-3 py-2 text-sm text-text focus:outline-none focus:border-primary resize-none"
                            />
                            <Button
                                variant="primary_s"
                                onClick={() => patch("notes", { name, sender_message: senderMessage })}
                                disabled={savingTab === "notes" || !dirty}
                                className="self-end"
                            >
                                {texts?.save}
                            </Button>
                        </div>
                        )
                    })()}

                    {tab === "theme" && (
                        <div className="pt-1 flex flex-col gap-3">
                            <Typography variant="txt2_dim" className="block">
                                {texts?.themeDesc}
                            </Typography>
                            <ThemeList
                                value={themeId}
                                onChange={(v) => {
                                    setThemeId(v)
                                    patch("theme", {
                                        presets: {
                                            theme_id: v && /^[0-9a-fA-F]{24}$/.test(v) ? v : null,
                                            theme_mode: v === ACCOUNT_DEFAULT_ID ? "account"
                                                : v === "__default__" ? "none"
                                                : "specific",
                                        },
                                    })
                                }}
                            />
                        </div>
                    )}

                    {tab === "access" && (
                        <div className="pt-1">
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { value: "public", label: texts?.accessPublic, desc: texts?.accessPublicDesc, icon: GlobeAltIcon },
                                    { value: "authenticated", label: texts?.accessAuthenticated, desc: texts?.accessAuthenticatedDesc, icon: LockClosedIcon },
                                ].map((opt) => {
                                    const Icon = opt.icon
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={async () => {
                                                setAccessType(opt.value)
                                                if (opt.value === "authenticated") setShowAccessDrawer(true)
                                                await patch("access", {
                                                    presets: {
                                                        access_type: opt.value,
                                                        access: opt.value === "authenticated" ? accessEmails : [],
                                                    },
                                                })
                                            }}
                                            className={`w-full rounded-xl border-2 p-3 text-left transition-colors ${
                                                accessType === opt.value
                                                    ? "border-primary bg-primary/10"
                                                    : "border-bordered hover:border-text_secondary"
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <Icon className="h-4 w-4 text-text_secondary" />
                                                <span className="text-sm font-medium text-text">{opt.label}</span>
                                            </div>
                                            <div className="text-xs text-text_secondary">{opt.desc}</div>
                                        </button>
                                    )
                                })}
                            </div>
                            {accessType === "authenticated" && (
                                <button
                                    type="button"
                                    onClick={() => setShowAccessDrawer((v) => !v)}
                                    className="mt-3 w-full flex items-center justify-between gap-2 rounded-full border border-primary px-4 py-2.5 text-sm transition-colors"
                                >
                                    <div className="flex items-center gap-2 text-primary">
                                        <UsersIcon className="hidden sm:block h-4 w-4" />
                                        <span>{texts?.manageUserAccess}</span>
                                    </div>
                                    <span className="text-xs bg-primary rounded-full px-2 py-0.5 text-white font-medium">
                                        {accessEmails.length === 0
                                            ? texts?.addUsers
                                            : `${accessEmails.length} ${accessEmails.length === 1 ? texts?.user : texts?.users}`}
                                    </span>
                                </button>
                            )}
                            {accessType === "authenticated" && showAccessDrawer && (
                                <div className="mt-3 bg-canvas border border-bordered rounded-2xl p-3">
                                    <UserAccessWidget
                                        emails={accessEmails}
                                        onAdd={async (email) => {
                                            const next = accessEmails.includes(email) ? accessEmails : [...accessEmails, email]
                                            setAccessEmails(next)
                                            await patch("access", { presets: { access_type: "authenticated", access: next } })
                                        }}
                                        onRemove={async (email) => {
                                            const next = accessEmails.filter((e) => e !== email)
                                            setAccessEmails(next)
                                            await patch("access", { presets: { access_type: "authenticated", access: next } })
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "password" && (
                        <div className="pt-1">
                            <div className="w-full flex items-center justify-between px-1 py-2 text-sm">
                                <div className="flex items-center gap-2">
                                    <KeyIcon className="h-4 w-4 text-text_secondary" />
                                    <span className="font-medium text-text">{texts?.passwordProtection}</span>
                                </div>
                                <ToggleSwitch
                                    isOn={passwordEnabled}
                                    onChange={async (val) => {
                                        setPasswordEnabled(val)
                                        if (!val) {
                                            setPassword("")
                                            setHasPassword(false)
                                            await patch("password", { presets: { password: "" } })
                                        }
                                    }}
                                />
                            </div>
                            {passwordEnabled && (
                                <div className="flex flex-col gap-2 mt-2">
                                    <div className="relative">
                                        <Input
                                            variant="transfer_compact"
                                            type={showPassword ? "text" : "password"}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onEnter={async () => {
                                                if (!password) return
                                                const ok = await patch("password", { presets: { password } })
                                                if (ok) { setHasPassword(true); setPassword("") }
                                            }}
                                            placeholder={hasPassword
                                                ? texts?.leaveBlankKeep
                                                : texts?.enterPassword}
                                            className="w-full pr-10"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text_secondary hover:text-text"
                                        >
                                            {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                        </button>
                                    </div>
                                    <Button
                                        variant="primary_s"
                                        onClick={async () => {
                                            const ok = await patch("password", { presets: { password } })
                                            if (ok) { setHasPassword(true); setPassword("") }
                                        }}
                                        disabled={savingTab === "password" || !password}
                                        className="self-end"
                                    >
                                        {texts?.save}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "properties" && (
                        <div className="grid grid-cols-2 gap-y-4 text-sm pt-1">
                            <div>
                                <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                                    <HashtagIcon className="h-3.5 w-3.5" />
                                    {texts?.tokenId}
                                </span>
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-text font-mono text-xs truncate" title={link.token}>
                                        {tokenShort}…
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            try {
                                                navigator.clipboard.writeText(link.token)
                                                showSuccessToast(texts?.tokenCopied, "", {
                                                    position: TOAST_POSITIONS.TOP_CENTER, pauseOnHover: false,
                                                    enableDismissalTracking: false, duration: 1200,
                                                })
                                            } catch {}
                                        }}
                                        className="p-1 rounded text-text_secondary hover:text-text hover:bg-neutral_1 transition-colors shrink-0"
                                    >
                                        <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div>
                                <span className="text-text_secondary text-xs flex items-center gap-1 mb-0.5">
                                    <InboxArrowDownIcon className="h-3.5 w-3.5" />
                                    {texts?.uploads}
                                </span>
                                <span className="text-text font-medium">
                                    {link.transfers_used || 0}
                                    {link.max_transfers != null && (
                                        <span className="text-xs ml-1 text-text_secondary">
                                            ({Math.max(0, link.max_transfers - (link.transfers_used || 0))} {texts?.left})
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div>
                                <span className="text-text_secondary text-xs mb-0.5 block">{texts?.created}</span>
                                <span className="text-text font-medium">{formatDate(link.created)}</span>
                            </div>
                            <div>
                                <span className="text-text_secondary text-xs mb-0.5 block">{texts?.expires}</span>
                                <span className="text-text font-medium">{formatDate(link.link_expires_at)}</span>
                            </div>
                            <div>
                                <span className="text-text_secondary text-xs mb-0.5 block">{texts?.retention}</span>
                                <span className="text-text font-medium">{link.presets?.retention_days || 7} {texts?.days}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AnimatePresence>
                {detailsTransfer && (
                    <motion.div
                        className="absolute inset-0 z-[20] bg-paper/95 backdrop-blur-md flex flex-col items-center justify-center p-6 cursor-pointer overflow-y-auto"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={() => setDetailsTransfer(null)}
                    >
                        <motion.div
                            className="w-full text-center flex flex-col items-center gap-5"
                            initial={{ y: 16, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 8, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                                <EnvelopeIcon className="w-7 h-7" />
                            </div>
                            <Typography variant="txt2_dim">
                                {detailsTransfer.sender_name || detailsTransfer.sender_email || (texts?.unknownSender)}
                            </Typography>
                            {detailsTransfer.sender_title && (
                                <h2 className="text-2xl md:text-3xl font-bold text-text leading-tight break-words">
                                    {detailsTransfer.sender_title}
                                </h2>
                            )}
                            {detailsTransfer.sender_notes && (
                                <p className="text-sm md:text-base text-text_secondary leading-relaxed whitespace-pre-wrap break-words">
                                    {detailsTransfer.sender_notes}
                                </p>
                            )}
                        </motion.div>
                        <span className="absolute bottom-4 left-0 right-0 text-center text-xs text-text_secondary pointer-events-none">
                            {texts?.closeHint}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>

            {inviteOpen && link && (
                <Modal
                    isOpen
                    onClose={() => setInviteOpen(false)}
                    variant="transfer_compact"
                    closeOnClickOutside={false}
                    closeOnEscape
                    zIndex={1000000004}
                    className="max-w-lg"
                >
                    <div className="p-5">
                        <LinkSender
                            embedded
                            sendFn={async (emails, message) => {
                                const res = await sendLinkInvite(link._id, emails, message)
                                if (res?.result) {
                                    // mirror outbound shape so LinkSender can show its
                                    // "X sent successfully" success state.
                                    return { recipients_sent: res.invited }
                                }
                                return res
                            }}
                            onSent={() => {
                                setInviteOpen(false)
                                onChanged?.({ ...link, presets: { ...link.presets } })
                            }}
                        />
                    </div>
                </Modal>
            )}

            {filesModal && (
                <Modal
                    isOpen
                    onClose={() => setFilesModal(null)}
                    title={`${filesModal.file_count} ${filesModal.file_count === 1 ? texts?.file_singular : texts?.file_plural}`}
                    variant="compact"
                    closeOnClickOutside
                    closeOnEscape
                    zIndex={1000000005}
                    footerContent={
                        <div className="flex justify-center px-4 pb-4 pt-2">
                            <button
                                type="button"
                                disabled={filesDownloading}
                                onClick={async () => {
                                    setFilesDownloading(true)
                                    try {
                                        const r = await PrepareDownloadShareAll(filesModal.share_token)
                                        if (r?.url) window.location.href = r.url
                                    } finally { setFilesDownloading(false) }
                                }}
                                className="inline-flex items-center justify-center gap-2 w-full max-w-xs h-[44px] rounded-full bg-primary text-white font-semibold hover:bg-primary_dk transition-colors disabled:opacity-60"
                            >
                                {filesDownloading
                                    ? <Spinner className="w-[16px] h-[16px]" />
                                    : <FolderArrowDownIcon className="h-4 w-4" />}
                                {filesDownloading ? texts?.preparingDownload : texts?.downloadAll}
                            </button>
                        </div>
                    }
                >
                    <div className="px-5 py-2">
                        <FileTree
                            entries={(filesModal.files || []).map((f, i) => ({ path: f.name, size: f.size, index: i }))}
                            onDownload={async (index) => {
                                // Per-file = signed direct URL (same as the
                                // recipient page). Owner bypasses password.
                                const res = await GetShareDownloadUrl(filesModal.share_token, null, index)
                                if (res?.url) window.location.href = res.url
                            }}
                            onDownloadFolder={async (path) => {
                                const r = await PrepareDownloadShareFolder(filesModal.share_token, null, path)
                                if (r?.url) window.location.href = r.url
                            }}
                            maxHeightClass="max-h-[50vh]"
                        />
                    </div>
                </Modal>
            )}
        </Drawer>
    )
}
